import { getQueryUrl } from 'utils';
import { apiVersion } from '../apis.const';
import { transformResponse } from '../apis.util';
import { baseApi } from '../base.api';

/**
 * Create APIs
 */
export const metricsApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchScansCount: build.query<FetchScansCountRes, FetchScansCountReq>({
      query: ({ orgSlug, buildingId, zoneId, start, end, type }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/scans/count`,
          queryObj: { buildingId, zoneId, start, end, type },
        }),
      }),
      transformResponse,
      providesTags: ['ScansCount'],
    }),

    fetchSubscribersCount: build.query<FetchSubscribersCountRes, FetchSubscribersCountReq>({
      query: ({ orgSlug, buildingId, zoneId }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/subscriber/count`,
          queryObj: { buildingId, zoneId },
        }),
      }),
      transformResponse,
      providesTags: ['SubscribersCount'],
    }),

    fetchScheduleProgress: build.query<FetchScheduleProgressRes, FetchScheduleProgressReq>({
      query: ({ orgSlug, buildingId, zoneId, date }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/schedule/progress`,
          queryObj: { buildingId, zoneId, date },
        }),
      }),
      transformResponse,
      providesTags: ['ScheduleProgress'],
    }),

    fetchRatingSummary: build.query<FetchRatingSummaryRes, FetchRatingSummaryReq>({
      query: ({ orgSlug, buildingId, start, end }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/rating/summary`,
          queryObj: { buildingId, start, end },
        }),
      }),
      transformResponse,
      providesTags: ['RatingSummary'],
    }),

    fetchTotalFeedbackReceived: build.query<FetchTotalFeedbackReceivedRes, FetchTotalFeedbackReceivedReq>({
      query: ({ orgSlug, buildingId, start, end }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/feedback/count`,
          queryObj: { buildingId, start, end },
        }),
      }),
      transformResponse,
      providesTags: ['TotalFeedbackReceived'],
    }),
    fetchCategoryRatingExtremes: build.query<FetchCategoryRatingExtremesRes, FetchCategoryRatingExtremesReq>({
      query: ({ orgSlug, buildingId, start, end }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/rating/category-extremes`,
          queryObj: { buildingId, start, end },
        }),
      }),
      transformResponse,
      providesTags: ['CategoryRatingExtremes'],
    }),
    fetchAreaRatingExtremes: build.query<FetchAreaRatingExtremesRes, FetchAreaRatingExtremesReq>({
      query: ({ orgSlug, buildingId, start, end }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/rating/area-extremes`,
          queryObj: { buildingId, start, end },
        }),
      }),
      transformResponse,
      providesTags: ['AreaRatingExtremes'],
    }),
    fetchCleaningHistory: build.query<FetchCleaningHistoryRes, FetchCleaningHistoryReq>({
      query: ({ orgSlug, buildingId, start, end, page }) => ({
        url: getQueryUrl({
          url: `/${orgSlug}/${apiVersion}/metrics/cleaning/history`,
          queryObj: { buildingId, start, end, page },
        }),
      }),
      transformResponse,
      providesTags: ['CleaningHistory'],
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchScansCountQuery,
  useFetchSubscribersCountQuery,
  useFetchScheduleProgressQuery,
  useFetchRatingSummaryQuery,
  useFetchTotalFeedbackReceivedQuery,
  useFetchCategoryRatingExtremesQuery,
  useFetchAreaRatingExtremesQuery,
  useFetchCleaningHistoryQuery,
} = metricsApi;

/**
 * Types
 */
type OrgSlug = {
  orgSlug: string;
};

type FetchScansCountRes = any;
type FetchScansCountReq = OrgSlug & {
  buildingId?: number;
  zoneId?: number;
  start: string;
  end: string;
  type?: string;
};
type FetchSubscribersCountRes = any;
type FetchSubscribersCountReq = OrgSlug & {
  buildingId?: number;
  zoneId?: number;
};
type FetchScheduleProgressRes = any;
type FetchScheduleProgressReq = OrgSlug & {
  buildingId?: number;
  zoneId?: number;
  date?: string;
};

type FetchRatingSummaryRes = any;
type FetchRatingSummaryReq = OrgSlug & {
  buildingId?: number;
  start: string;
  end: string;
};

type FetchTotalFeedbackReceivedRes = any;
type FetchTotalFeedbackReceivedReq = OrgSlug & {
  buildingId?: number;
  start: string;
  end: string;
};

type FetchCategoryRatingExtremesRes = any;
type FetchCategoryRatingExtremesReq = OrgSlug & {
  buildingId?: number;
  start: string;
  end: string;
};

type FetchAreaRatingExtremesRes = any;
type FetchAreaRatingExtremesReq = OrgSlug & {
  buildingId?: number;
  start: string;
  end: string;
};

type FetchCleaningHistoryRes = any;
type FetchCleaningHistoryReq = OrgSlug & {
  buildingId?: number;
  start: string;
  end: string;
  page: number;
};
